import { createSlice } from "@reduxjs/toolkit";
import { getUserProfile } from "@/state/profileSlice";
import { setUser } from "@/auth/authSlice";
import { defaultTrailerType, defaultRadiusType } from "@/utils/constants";
import { protectedPaths } from "@/routing/paths";
import { today, tomorrow } from "@/utils/date";

const initialSearchType =
  window.location.pathname === protectedPaths[1]
    ? "load_rates"
    : window.location.pathname === protectedPaths[2]
      ? "market_potential"
      : window.location.pathname === protectedPaths[0]
        ? "loadboard" // Default to loadboard on homepage (will update when profile loads if user is not locked)
        : window.location.pathname === protectedPaths[4]
          ? "company_analytics"
          : window.location.pathname === protectedPaths[8]
            ? "load_finder"
            : window.location.pathname === protectedPaths[10]
              ? "broker_analytics"
              : null;

const initialState = {
  buttonFocus: false,
  pickup: null,
  delivery: null,
  pickupQuery: "",
  deliveryQuery: "",
  pickupRadius: null,
  deliveryRadius: null,
  trailerType: defaultTrailerType,
  outboundSearch: false,
  pickupDate: today,
  pickupDateEnd: null,
  deliveryDate: tomorrow,
  deliveryDateEnd: null,
  searchType: initialSearchType,
  referenceId: "",
  swapPending: false,
};

const searchInputSlice = createSlice({
  name: "searchInput",
  initialState,
  reducers: {
    toggleFocusOnButton: (state) => {
      state.buttonFocus = !state.buttonFocus;
    },
    setPickup: (state, action) => {
      state.pickup = action.payload;
    },
    setDelivery: (state, action) => {
      state.delivery = action.payload;
    },
    setPickupQuery: (state, action) => {
      state.pickupQuery = action.payload;
    },
    setDeliveryQuery: (state, action) => {
      state.deliveryQuery = action.payload;
    },
    setPickupDate: (state, action) => {
      state.pickupDate = action.payload;
    },
    setPickupDateEnd: (state, action) => {
      state.pickupDateEnd = action.payload;
    },
    setDeliveryDate: (state, action) => {
      state.deliveryDate = action.payload;
    },
    setDeliveryDateEnd: (state, action) => {
      state.deliveryDateEnd = action.payload;
    },
    setTrailerType: (state, action) => {
      state.trailerType = action.payload;
    },
    setPickupRadius: (state, action) => {
      state.pickupRadius = action.payload;
    },
    setDeliveryRadius: (state, action) => {
      state.deliveryRadius = action.payload;
    },
    setSearchType: (state, action) => {
      state.searchType = action.payload;
      // Switching search type dismisses any pending swap overlay.
      state.swapPending = false;
    },
    setReferenceId: (state, action) => {
      state.referenceId = action.payload;
    },
    clearInputs: (state) => {
      // The selected tab is navigation state, not an input value, so it survives a clear.
      // (initialState.searchType is derived from the URL at module load and is stale here.)
      return { ...initialState, searchType: state.searchType };
    },
    clearInputsExceptFixedDropdowns: (state) => {
      const { trailerType: _t, pickupRadius: _p, deliveryRadius: _d, searchType: _s, ...rest } = initialState;
      return {
        ...state,
        ...rest,
      };
    },
    clearInputsForLoadboard: () => {
      // Reset all inputs and set searchType to loadboard
      // Note: Do NOT spread initialState here as it's computed at module load time
      // and may have the wrong searchType if the app was loaded on a different page
      return {
        buttonFocus: false,
        pickup: null,
        delivery: null,
        pickupQuery: "",
        deliveryQuery: "",
        pickupRadius: defaultRadiusType,
        deliveryRadius: defaultRadiusType,
        trailerType: defaultTrailerType,
        outboundSearch: false,
        pickupDate: null,
        pickupDateEnd: null,
        deliveryDate: null,
        deliveryDateEnd: null,
        searchType: "loadboard",
        swapPending: false,
      };
    },
    setInputSearch: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    swapInputs: (state, action) => {
      // Swap pickup <-> delivery location fields. `pending` controls the dim overlay:
      // true when the user clicks swap, false when reverting (overlay click).
      const pending = action.payload?.pending ?? true;
      const { pickup, delivery, pickupQuery, deliveryQuery, pickupRadius, deliveryRadius } = state;
      state.pickup = delivery;
      state.delivery = pickup;
      state.pickupQuery = deliveryQuery;
      state.deliveryQuery = pickupQuery;
      state.pickupRadius = deliveryRadius;
      state.deliveryRadius = pickupRadius;
      state.swapPending = pending;
    },
    clearSwapPending: (state) => {
      state.swapPending = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.trailerType = action.payload.trailerType;
      })
      .addCase(setUser, (state, action) => {
        // Set default search type based on featuresUnlocked when on homepage
        if (window.location.pathname === protectedPaths[0]) {
          state.searchType = action.payload.featuresUnlocked ? "load_rates" : "loadboard";
        }
      });
  },
});

export const {
  toggleFocusOnButton,
  setPickup,
  setDelivery,
  setPickupQuery,
  setDeliveryQuery,
  setPickupRadius,
  setDeliveryRadius,
  setPickupDate,
  setPickupDateEnd,
  setDeliveryDate,
  setDeliveryDateEnd,
  setTrailerType,
  setSearchType,
  setReferenceId,
  clearInputs,
  clearInputsForLoadboard,
  clearInputsExceptFixedDropdowns,
  setInputSearch,
  swapInputs,
  clearSwapPending,
} = searchInputSlice.actions;
export default searchInputSlice.reducer;
